import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Layers, Zap } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const CHANNELS = ["LinkedIn", "X", "Facebook", "Instagram", "Email"];

export const AutomationCard = ({ value, onSaved }) => {
  const { user } = useAuth();
  const canEdit = ["owner", "admin"].includes(user?.role);
  const [a, setA] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => setA(value), [value]);
  const toggle = (c) => setA({ ...a, channels: a.channels.includes(c) ? a.channels.filter((x) => x !== c) : [...a.channels, c] });
  const save = async () => {
    setBusy(true);
    try { await api.put("/app/intel/automation", a); toast.success(a.mode === "auto" ? "Automatic Monday bulk repurpose enabled" : "Set to manual"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const est = 2 * a.channels.length * a.max_themes;
  return (
    <div className="card-box p-6 space-y-4" data-testid="bulk-automation-card">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2"><Zap className="h-4 w-4 text-[#f59e0b]" /> Bulk repurpose</h3>
        <label className="flex items-center gap-2 text-sm">
          <span className={a.mode === "auto" ? "font-semibold text-[#10b981]" : "text-muted-foreground"}>{a.mode === "auto" ? "Automatic · Mon 7 AM" : "Manual"}</span>
          <Switch checked={a.mode === "auto"} disabled={!canEdit} onCheckedChange={(v) => setA({ ...a, mode: v ? "auto" : "manual" })} data-testid="bulk-mode-switch" />
        </label>
      </div>
      <p className="text-xs text-muted-foreground">Automatic: every Monday the agent generates the weekly intelligence report and turns its top insights into ready-to-approve posts. Manual: use "Bulk repurpose" on any report.</p>
      <div className="flex flex-wrap gap-2">{CHANNELS.map((c) => (
        <button key={c} type="button" disabled={!canEdit} onClick={() => toggle(c)} data-testid={`bulk-channel-${c}`}
          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${a.channels.includes(c) ? "bg-brand text-white border-transparent" : "hover:bg-foreground/5"}`}>{c}</button>))}
      </div>
      <div className="flex items-center gap-3 text-sm">
        <span>Insights per report</span>
        <select disabled={!canEdit} value={a.max_themes} onChange={(e) => setA({ ...a, max_themes: Number(e.target.value) })} className="rounded-lg border bg-transparent px-2 py-1" data-testid="bulk-themes-select">
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="ml-auto text-xs text-muted-foreground" data-testid="bulk-estimate">≈ {a.channels.length * a.max_themes} posts · {est} cr</span>
      </div>
      {canEdit ? <button disabled={busy || !a.channels.length} onClick={save} className="btn-brand w-full" data-testid="bulk-save-btn">{busy ? "Saving..." : "Save automation"}</button>
        : <p className="text-xs text-muted-foreground" data-testid="bulk-owner-only">Only the account owner can change this setting.</p>}
    </div>
  );
};

export const BulkButton = ({ reportId, done, onDone }) => {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/app/intel/reports/${reportId}/bulk-repurpose`, {});
      toast.success(`${data.items.length} posts sent to Approvals · ${data.credits_charged} credits`);
      refresh(); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button disabled={busy} onClick={run} data-testid={`bulk-repurpose-${reportId}`} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-foreground/5 text-[#7c3aed] border-[#7c3aed]/40 disabled:opacity-50">
      <Layers className="h-3.5 w-3.5" />{busy ? "Creating..." : done ? `Bulk again (${done} done)` : "Bulk repurpose"}
    </button>
  );
};
